import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import DynamicForm from "../../../components/DynamicForm";
import FormSkeletonLoader from "../../../components/FormSkeletonLoader";
import {
  manageBackendUserFields,
  manageBackendUserValidationSchema,
} from "../../../Schema/BackendUserSchema";
import {
  useGetBackendUserDetails,
  useUpdateBackendUser,
  useDeleteBackendUser,
} from "../../../custom-hooks/queries";
import NotificationModal from "../../../components/NotificationModal";
import Modal from "../../../components/Modal";
import CustomButton from "../../../components/CustomButton";

export const CLERK_NOT_SYNCED_MESSAGE =
  "User updated. The user's login could not be updated because it no longer exists in the identity provider.";

const ManageBackendUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);

  const { data: userDetails, isLoading } = useGetBackendUserDetails(id);
  const { mutate: updateUser, isError } = useUpdateBackendUser();
  const {
    mutate: deleteUser,
    isLoading: isDeleting,
    isError: deleteError,
  } = useDeleteBackendUser();

  const defaultValues = {
    email: userDetails?.email || "",
    firstName: userDetails?.first_name || "",
    middleName: userDetails?.middle_name || "",
    lastName: userDetails?.last_name || "",
    phoneNumber: userDetails?.phone_number || "",
    officePhoneNumber: userDetails?.office_phone_number || "",
    contactPreference: userDetails?.contact_preference || "Office_Phone",
    isActive: userDetails?.is_active,
    rights: [
      "Providers Released RFI",
      "Manage RFIs",
      "Manage Provider",
      "Manage Sponsor",
      "View Sponsor Activity",
      "View Provider Activity",
      "Manage User",
      "Standards",
      "Manage Provider RFIs",
      "Request Assessment",
      "Auditor Management",
      "Assessment Calendar",
      "Assessment Logs",
      "Receive Notifications",
    ],
  };

  const onSubmit = (data) => {
    const updateData = {
      first_name: data.firstName,
      middle_name: data.middleName,
      last_name: data.lastName,
      phone_number: data.phoneNumber,
      office_phone_number: data.officePhoneNumber,
      contact_preference: data.contactPreference,
      is_active: data.isActive,
      user_permissions: {
        view_released_rfi: data.rights.includes("Providers Released RFI"),
        manage_rfi: data.rights.includes("Manage RFIs"),
        manage_provider: data.rights.includes("Manage Provider"),
        manage_sponsor: data.rights.includes("Manage Sponsor"),
        view_sponsor_activity: data.rights.includes("View Sponsor Activity"),
        view_provider_activity: data.rights.includes("View Provider Activity"),
        manage_backend_users: data.rights.includes("Manage User"),
        manage_standards: data.rights.includes("Standards"),
        manage_provider_rfi: data.rights.includes("Manage Provider RFIs"),
        request_assessment: data.rights.includes("Request Assessment"),
        auditor_management: data.rights.includes("Auditor Management"),
        view_assessment_calendar: data.rights.includes("Assessment Calendar"),
        view_assessment_logs: data.rights.includes("Assessment Logs"),
        receive_notifications: data.rights.includes("Receive Notifications"),
      },
    };

    updateUser(
      { id, userData: updateData },
      {
        onSuccess: (result) => {
          // clerk_synced=false: the DB row was saved but the user's Clerk login is gone
          // or Clerk failed, so the name/active state did not reach the identity provider.
          setMessage(
            result?.clerk_synced === false
              ? CLERK_NOT_SYNCED_MESSAGE
              : "User updated successfully"
          );
          setShow(true);
          setTimeout(() => navigate("/Backend/Users/BackendUsers"), 2000);
        },
        onError: (error) => {
          setMessage(error.response?.data?.message || "Failed to update user");
          setShow(true);
        },
      }
    );
  };

  const handleDelete = () => {
    deleteUser(id, {
      onSuccess: () => {
        setMessage("User deleted successfully");
        setShow(true);
        setDeleteModalOpen(false);
        setTimeout(() => navigate("/Backend/Users/BackendUsers"), 2000);
      },
      onError: (error) => {
        setMessage(error.response?.data?.message || "Failed to delete user");
        setShow(true);
        setDeleteModalOpen(false);
      },
    });
  };

  if (isLoading) {
    return <FormSkeletonLoader />;
  }

  // Create a modified copy of the fields with email disabled
  const modifiedFields = manageBackendUserFields.map((field) => {
    if (field.name === "email") {
      return { ...field, disabled: true, readOnly: true };
    }
    return field;
  });

  return (
    <div className="w-full">
      <DynamicForm
        formTitle="Update Backend User"
        formDescription="Update the information below to modify the Backend User details."
        fields={modifiedFields}
        validationSchema={manageBackendUserValidationSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        isUpdate={true}
        className="w-full"
        onDelete={() => setDeleteModalOpen(true)}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title="Delete User"
        description="Are you sure you want to delete this user? This action cannot be undone."
        textBtn="Delete"
        onClick={handleDelete}
        loading={isDeleting}
        color="bg-red-400"
        hoverColor="hover:bg-red-700"
      />

      {/* Notification Modal */}
      <NotificationModal
        open={show}
        setOpen={setShow}
        desc={message}
        title={isError || deleteError ? "Error" : "Success"}
        error={isError || deleteError}
      />
    </div>
  );
};

export default ManageBackendUser;
